<template>
    <VJoyWrapper no-margin direction="column" align="stretch" row-gap="8">
        <VJoyWrapper no-margin direction="column" align="stretch" row-gap="3">
            <VJoyFormGroup ref="locationPreferencesRef">
                <VJoyLabel :text="t('profile.section.workplace.preferences.edit.location.subtitle')" size="small" />
                <VJoyRadioGroup
                    data-testid="profile-workplace-preferences-locations-options"
                    class="profile-workplace-preferences-edition__radio-group"
                >
                    <VJoyRadio
                        v-for="option in locationOptions"
                        :key="option.value"
                        v-model="state.isRemoteOnly"
                        :value="option.value"
                        :data-testid="option.testid"
                        theme="outline"
                    >
                        {{ option.label }}
                    </VJoyRadio>
                </VJoyRadioGroup>
            </VJoyFormGroup>
            <VJoyWrapper
                v-if="state.isRemoteOnly === 'false'"
                class="profile-workplace-preferences-edition__wrapper"
                no-margin
                direction="column"
                align="flex-start"
                row-gap="3"
            >
                <VJoyWrapper
                    class="profile-workplace-preferences-edition__item"
                    no-margin
                    wrap="nowrap"
                    direction="row"
                    align="stretch"
                    column-gap="4"
                >
                    <VJoyInput
                        :model-value="profile?.locationPreferences?.address?.formattedAddress ?? t('generic.unknown')"
                        class="profile-workplace-preferences-edition__item-address"
                        :label="t('profile.section.workplace.preferences.edit.location.city.subtitle')"
                        label-size="small"
                        icon="location"
                        type="text"
                        name="principalAddress"
                        :disabled="true"
                    />
                    <VJoySelect
                        v-model="r$.principalAddressRadius.$value"
                        :label="t('profile.section.workplace.preferences.edit.location.radius.subtitle')"
                        label-size="small"
                        class="profile-workplace-preferences-edition__item-radius"
                    >
                        <option disabled :value="null">
                            {{ t('profile.section.workplace.preferences.radius.title') }}
                        </option>
                        <option v-for="radius in availableCityRadius" :key="`${radius.value}`" :value="radius.value">
                            {{ radius.label }}
                        </option>
                    </VJoySelect>
                    <div class="profile-workplace-preferences-edition__item-button" />
                </VJoyWrapper>
                <VJoyWrapper
                    v-for="(item, index) in r$.alternativeAddresses.$each"
                    :key="item.$id"
                    class="profile-workplace-preferences-edition__wrapper"
                    no-margin
                    direction="column"
                    align="stretch"
                    row-gap="3"
                >
                    <VJoyWrapper
                        class="profile-workplace-preferences-edition__item"
                        no-margin
                        wrap="nowrap"
                        direction="row"
                        align="stretch"
                        column-gap="4"
                    >
                        <LocationAutocomplete
                            v-model="item.$value.address"
                            :disabled="false"
                            :invalid="item.$error"
                            class="profile-workplace-preferences-edition__item-location"
                        />
                        <VJoySelect v-model="item.$value.radius" class="profile-workplace-preferences-edition__item-radius">
                            <option disabled :value="null">
                                {{ t('profile.section.workplace.preferences.radius.title') }}
                            </option>
                            <option v-for="radius in availableCityRadius" :key="`${radius.value}`" :value="radius.value">
                                {{ radius.label }}
                            </option>
                        </VJoySelect>
                        <VJoyIconButton
                            class="profile-workplace-preferences-edition__item-button"
                            size="small"
                            icon="trash"
                            variant="ghost"
                            :label="$t('general.delete')"
                            @click="removeAlternativeAddress(item.$id)"
                        />
                    </VJoyWrapper>
                    <div v-if="r$.alternativeAddresses.$each[index].$error" class="profile-workplace-preferences-edition__error">
                        <VJoyFormError v-for="error of [...r$.$errors.alternativeAddresses.$each[index].address.city]" :key="error" no-margin>
                            {{ error }}
                        </VJoyFormError>
                    </div>
                </VJoyWrapper>
                <div v-if="r$.alternativeAddresses.$error" class="profile-workplace-preferences-edition__error">
                    <VJoyFormError v-for="error of r$.$errors.alternativeAddresses.$self" :key="error" no-margin>
                        {{ error }}
                    </VJoyFormError>
                </div>
                <VJoyButton
                    id="profile-workplace-preferences-edition-add-location"
                    icon="add"
                    variant="secondary"
                    :disabled="isAlternativeAddressesAddButtonDisabled"
                    @click="addAlternativeAddress"
                >
                    {{ t('profile.section.workplace.preferences.edit.add.location') }}
                </VJoyButton>
            </VJoyWrapper>
        </VJoyWrapper>
        <VJoyWrapper v-if="isStrategyProfile" no-margin direction="column" align="stretch" row-gap="3">
            <VJoyFormGroup ref="travelPreferencesRef">
                <VJoyLabel :text="t('profile.section.workplace.preferences.edit.travel.subtitle')" size="small" />
                <VJoyRadioGroup data-testid="profile-workplace-preferences-travel-options" class="profile-workplace-preferences-edition__radio-group">
                    <VJoyRadio
                        v-for="option in travelOptions"
                        :key="option.value"
                        v-model="state.isWillingToTravel"
                        :value="option.value"
                        :data-testid="option.testid"
                        theme="outline"
                    >
                        {{ option.label }}
                    </VJoyRadio>
                </VJoyRadioGroup>
            </VJoyFormGroup>
            <VJoyWrapper
                v-if="state.isWillingToTravel === 'true'"
                class="profile-workplace-preferences-edition__wrapper"
                no-margin
                direction="column"
                align="flex-start"
                row-gap="3"
            >
                <VJoyWrapper
                    v-for="(item, index) in r$.travelLocations.$each"
                    :key="item.$id"
                    class="profile-workplace-preferences-edition__item"
                    no-margin
                    direction="column"
                    align="flex-start"
                    row-gap="3"
                >
                    <VJoyWrapper
                        class="profile-workplace-preferences-edition__wrapper"
                        no-margin
                        wrap="nowrap"
                        direction="row"
                        align="stretch"
                        column-gap="4"
                    >
                        <VJoyInput
                            v-model="item.value.$value"
                            class="profile-workplace-preferences-edition__item-travel"
                            :label="index === 0 ? t('profile.section.workplace.preferences.edit.travel.location.subtitle') : undefined"
                            label-size="small"
                            :invalid="item.$error"
                            :name="`travelLocation-${item.$id}`"
                            :placeholder="t('profile.section.workplace.preferences.edit.travel.placeholder')"
                        />
                        <VJoyIconButton
                            v-if="index !== 0"
                            class="profile-workplace-preferences-edition__item-button"
                            size="small"
                            icon="trash"
                            variant="ghost"
                            :label="$t('general.delete')"
                            @click="removeTravelPreference(item.$id)"
                        />
                        <div v-else class="profile-workplace-preferences-edition__item-button" />
                    </VJoyWrapper>
                    <div v-if="r$.travelLocations.$each[index].$error" class="profile-workplace-preferences-edition__error">
                        <VJoyFormError v-for="error of r$.$errors.travelLocations.$each[index].value" :key="error">
                            {{ error }}
                        </VJoyFormError>
                    </div>
                </VJoyWrapper>
                <div v-if="r$.travelLocations.$error" class="profile-workplace-preferences-edition__error">
                    <VJoyFormError v-for="error of r$.$errors.travelLocations.$self" :key="error">
                        {{ error }}
                    </VJoyFormError>
                </div>
                <VJoyButton
                    v-if="$ff('enable-willingness-to-travel-details')"
                    id="profile-workplace-preferences-edition-add-travel"
                    icon="add"
                    variant="secondary"
                    :disabled="isTravelLocationsAddButtonDisabled"
                    @click="addTravelPreference"
                >
                    {{ t('profile.section.workplace.preferences.edit.add.travel') }}
                </VJoyButton>
            </VJoyWrapper>
        </VJoyWrapper>
    </VJoyWrapper>
</template>
<script setup lang="ts">
    import {type ComponentPublicInstance, computed, nextTick, ref, useTemplateRef, watch} from 'vue';
    import {maxLength, required, withMessage, withParams} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {useStrategyProfile, useTranslation} from '#imports';
    import {
        VJoyButton,
        VJoyIconButton,
        VJoyFormError,
        VJoyInput,
        VJoySelect,
        VJoyWrapper,
        VJoyRadioGroup,
        VJoyRadio,
        VJoyLabel,
        VJoyFormGroup,
    } from '@maltjoy/core-vue';
    import LocationAutocomplete from '~/components/LocationAutocomplete.vue';
    import {
        type AlternativeAddressEditionItem,
        toAlternativeAddress,
        toTravelLocationEditionItem,
        type TravelLocationEditionItem,
        useScopedRegleItem,
    } from '~/features/about/types/EditAboutSection';
    import {useProfileStore} from '~/store/profileStore';
    import {useRulesStore} from '~/store/rules/rulesStore';
    import type {AddressResource} from '~/types/api-raw-types';

    const {profile} = storeToRefs(useProfileStore());
    const {alternativeAddressesCount, travelLocationsCount} = storeToRefs(useRulesStore());

    const {t} = useTranslation();

    const {hasInvalidAlternativeAddresses} = defineProps<{
        hasInvalidAlternativeAddresses: boolean;
    }>();

    const locationPreferencesRef = useTemplateRef<ComponentPublicInstance>('locationPreferencesRef');
    const travelPreferencesRef = useTemplateRef<ComponentPublicInstance>('travelPreferencesRef');

    const availableCityRadius = [
        {value: 50, label: t('profile.section.workplace.preferences.radius.label', {0: 50})},
        {value: 40, label: t('profile.section.workplace.preferences.radius.label', {0: 40})},
        {value: 30, label: t('profile.section.workplace.preferences.radius.label', {0: 30})},
        {value: 20, label: t('profile.section.workplace.preferences.radius.label', {0: 20})},
        {value: 10, label: t('profile.section.workplace.preferences.radius.label', {0: 10})},
    ];

    const locationOptions: {label: string; value: string; testid: string}[] = [
        {
            label: t('profile.section.workplace.preferences.remote.title'),
            value: 'true',
            testid: 'profile-workplace-preferences-locations-option-remote-only',
        },
        {
            label: t('profile.section.workplace.preferences.onsite.title'),
            value: 'false',
            testid: 'profile-workplace-preferences-locations-option-on-site',
        },
    ];

    const travelOptions: {label: string; value: string; testid: string}[] = [
        {
            label: t('general.yes'),
            value: 'true',
            testid: 'profile-workplace-preferences-travel-option-true',
        },
        {
            label: t('general.no'),
            value: 'false',
            testid: 'profile-workplace-preferences-travel-option-false',
        },
    ];

    const isStrategyProfile = computed(() => useStrategyProfile() || false);
    const isAlternativeAddressesAddButtonDisabled = computed(() => state.value.alternativeAddresses.length >= alternativeAddressesCount.value.max);
    const isTravelLocationsAddButtonDisabled = computed(() => state.value.travelLocations.length >= travelLocationsCount.value.max);

    const rules = computed(() => ({
        alternativeAddresses: {
            maxLength: withMessage(
                maxLength(alternativeAddressesCount.value.max),
                t('profile.section.workplace.preferences.edit.location.error.max', {0: alternativeAddressesCount.value.max}),
            ),
            isFromSameCountry: withMessage(
                // Used withParams to ensure that changes on hasInvalidAlternativeAddresses are correctly tracked
                withParams(() => !hasInvalidAlternativeAddresses, [() => hasInvalidAlternativeAddresses]),
                t('profile.section.workplace.preferences.edit.location.error.multiple.countries'),
            ),
            $each: {
                address: {
                    city: {
                        required,
                        hasNoDuplicates: withMessage(
                            (value) =>
                                state.value.alternativeAddresses
                                    .filter((item) => item?.address?.city && item?.address?.city.length > 0)
                                    .filter((item) => item?.address?.city.toLowerCase() === (value as string).toLowerCase()).length < 2,
                            t('profile.section.workplace.preferences.edit.location.error.duplicated'),
                        ),
                    },
                },
            },
        },
        travelLocations: {
            maxLength: withMessage(
                maxLength(travelLocationsCount.value.max),
                t('profile.section.workplace.preferences.edit.travel.error.max', {0: travelLocationsCount.value.max}),
            ),
            $each: {
                value: {
                    required,
                    hasNoDuplicates: withMessage(
                        (value) =>
                            state.value.travelLocations
                                .filter((item) => item?.value && item?.value.length > 0)
                                .filter((item) => item?.value.toLowerCase() === (value as string).toLowerCase()).length < 2,
                        t('profile.section.workplace.preferences.edit.travel.error.duplicated'),
                    ),
                },
            },
        },
    }));
    const defaultState = computed(() => ({
        isRemoteOnly: profile.value?.locationPreferences?.preference === 'REMOTE' ? 'true' : 'false',
        isWillingToTravel: profile.value?.locationPreferences?.willingnessToTravelInternationally ? 'true' : 'false',
        principalAddressRadius: profile.value?.locationPreferences?.addressRadius ?? 50,
        alternativeAddresses: profile.value?.locationPreferences?.activityLocations?.map((item) => toAlternativeAddress(item)) ?? [],
        travelLocations: profile.value?.locationPreferences?.travelLocations?.map((item) => toTravelLocationEditionItem(item)) ?? [],
    }));
    const state = ref({...defaultState.value});
    const {r$} = useScopedRegleItem(state, rules, {id: 'workplacePreferencesEdition'});

    async function addAlternativeAddress() {
        const emptyAddress: AddressResource = {
            formattedAddress: '',
            street: '',
            street2: '',
            city: '',
            zipCode: '',
            administrativeAreaLevel4: '',
            administrativeAreaLevel4Code: '',
            administrativeAreaLevel3: '',
            administrativeAreaLevel3Code: '',
            administrativeAreaLevel2: '',
            administrativeAreaLevel2Code: '',
            administrativeAreaLevel1: '',
            administrativeAreaLevel1Code: '',
            country: '',
            countryCode: '',
            region: '',
            loc: {
                lat: 0,
                lon: 0,
            },
        };
        const newAlternativeAddress: AlternativeAddressEditionItem = {
            address: emptyAddress,
            radius: 10,
        };
        state.value.alternativeAddresses = [...state.value.alternativeAddresses, newAlternativeAddress];
        await scrollToLocationPreferences();
    }

    function removeAlternativeAddress(idToRemove: string | undefined) {
        state.value.alternativeAddresses = r$.alternativeAddresses.$each.filter((item) => item.$id !== idToRemove).map((item) => item.$value);
        r$.$reset();
    }

    async function addTravelPreference() {
        const emptyValue: TravelLocationEditionItem = {
            value: '',
        };
        state.value.travelLocations = [...state.value.travelLocations, emptyValue];
        await scrollToTravelPreferences();
    }

    function removeTravelPreference(idToRemove: string | undefined) {
        state.value.travelLocations = r$.travelLocations.$each.filter((item) => item.$id !== idToRemove).map((item) => item.$value);
        r$.$reset();
    }

    watch(
        () => state.value.isRemoteOnly,
        async (value) => {
            if (value === 'false') {
                await scrollToLocationPreferences();
            }
        },
    );

    watch(
        () => state.value.isWillingToTravel,
        async (value) => {
            if (value === 'true') {
                await scrollToTravelPreferences();
            }
        },
    );

    async function scrollToLocationPreferences() {
        await nextTick();
        locationPreferencesRef.value?.$el.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    async function scrollToTravelPreferences() {
        await nextTick();
        travelPreferencesRef.value?.$el.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    .profile-workplace-preferences-edition {
        &__subtitle {
            font-size: var(--joy-font-size-primary-300);
            font-weight: var(--joy-font-weight-bold);
        }

        &__radio-group {
            :deep(.joy-radio-group-container) {
                margin-bottom: var(--joy-core-spacing-1);
                flex-wrap: nowrap;
            }
        }

        &__wrapper {
            width: 100%;
        }

        &__item {
            width: 100%;
            :deep(.joy-input--medium) {
                width: 100%;
            }
        }

        &__item-label {
            margin-bottom: 0;
        }

        &__item-address {
            flex: 0 0 50%;
        }

        &__item-radius {
            flex: 0 0 38%;
        }

        &__item-travel {
            width: 100%;
        }

        &__item-button {
            height: 48px;
            flex: 0 0 48px;
            @include mq.screen_xs {
                flex: 0 0 30px;
            }
        }
    }
</style>
